import { useForm, Controller, type SubmitHandler } from "react-hook-form"
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'

import { USER_ROLE_LABEL_MAP } from "@/constants/general"
import Form from "@/components/forms/reusables/form"
import useFormStates from "@/hooks/use-form-states"
import RHFTextfield from "@/components/form-fields/rhf-textfield"
import { UserRole, type UserFormInput } from "@/types"
import { useCreateUser } from "@/hooks/api/use-users"
import MunicipalitiesLoader from "@/wrappers/municipalities-loader"
import RHFPassword from "@/components/form-fields/rhf-password"
import { getUserRole } from "@/helpers"
import CollectionCompaniesLoader from "@/wrappers/collection-companies-loader"

const userRole = getUserRole()

const tieredRoles = {
    [UserRole.admin]: [
        UserRole.admin,
        UserRole.collectionCompany,
        UserRole.municipalityOfficial,
    ],
    [UserRole.collectionCompany]: [
        UserRole.collectionCompany,
        UserRole.municipalityOfficial,
    ],
    [UserRole.eokoDataAdmin]: [
        UserRole.collectionCompany,
        UserRole.municipalityOfficial,
        UserRole.eokoDataAdmin,
    ],
    [UserRole.municipalityOfficial]: [UserRole.municipalityOfficial],
    [UserRole.basic]: [],
    [UserRole.collectionPointAdmin]: [],
    [UserRole.resident]: [],
}

const UserForm = ({
    closeCallback,
} : {
    closeCallback?: () => void,
}) => {

    const initialValues = {
        email: '',
        password: '',
        role: UserRole.municipalityOfficial,
        municipality_id: '',
        collection_company_id: '',
    }
    
    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
        watch,
        setValue,
    } = useForm({
        defaultValues: { ...initialValues },
    })

    const {
        resetWithVersion,
        requestInProgress,
        setRequestInProgress,
        error,
        setError,
    } = useFormStates(() => reset(initialValues))

    const mutationCreate = useCreateUser(
        {
            setRequestInProgress,
            onSuccessCallback: closeCallback,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            }
        }
    )

    const onSubmit: SubmitHandler<UserFormInput> = data => {
        setRequestInProgress(true)
        mutationCreate.mutate(data)
    }

    const collectionCompanyId = watch('collection_company_id')

    return (
        <Form
            onSubmit={handleSubmit(onSubmit)}
            heading={'Vytvořit nového uživatele'}
            closeCallback={closeCallback}
            isLoadedFromLocalStorage={false}
            isDirty={isDirty}
            resetWithVersion={resetWithVersion}
            requestInProgress={requestInProgress}
            error={error}
            setError={setError}
        >
            <RHFTextfield
                name="email"
                control={control}
                label="Email"
                required
                type="email"
            />
            <RHFPassword
                name="password"
                control={control}
                label="Heslo"
                required
            />
            <FormControl fullWidth>
                <InputLabel id="role-select-label">Role</InputLabel>
                <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                        <Select {...field} label="Role">
                            {tieredRoles[userRole].map(role => (
                                <MenuItem key={role} value={role}>{USER_ROLE_LABEL_MAP[role]}</MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>
            <CollectionCompaniesLoader>
                {collectionCompanies => (
                    <FormControl fullWidth>
                        <InputLabel id="municipality-select-label">Svozová společnost</InputLabel>
                        <Controller
                            name="collection_company_id"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    label="Svozová společnost"
                                    onChange={(e) => {
                                        field.onChange(e)
                                        setValue("municipality_id", '')
                                    }}
                                >
                                    <MenuItem key="none" value="">-</MenuItem>
                                    {collectionCompanies.map(collectionCompany => (
                                        <MenuItem key={collectionCompany.id} value={collectionCompany.id}>{collectionCompany.eoko_customer?.name}</MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </FormControl>
                )}
            </CollectionCompaniesLoader>
            <MunicipalitiesLoader collectionCompanyId={collectionCompanyId}>
                {municipalities => (
                    <FormControl fullWidth>
                        <InputLabel id="municipality-select-label">Obec</InputLabel>
                        <Controller
                            name="municipality_id"
                            control={control}
                            render={({ field }) => (
                                <Select {...field} label="Obec">
                                    <MenuItem key="none" value="">-</MenuItem>
                                    {municipalities.map(municipality => (
                                        <MenuItem key={municipality.id} value={municipality.id}>{municipality.name}</MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </FormControl>
                )}
            </MunicipalitiesLoader>
        </Form>
    )
}

export default UserForm
